/**
 * video-text - the row 410 Video (test) tab's exact-text preview and note rule, on the SCREEN.
 *
 * MIRROR of api/src/lib/video-links.ts (composeVideoText, checkNote, NOTE_MAX), pinned in LOCKSTEP by
 * tests/unit/video-links.test.ts case by case: the tab composes the preview locally so it updates as
 * the note is typed, and the API composes the same sentence for the brake's duplicate rule and for
 * its own preview on GET /api/care/video-test. The two must never disagree, because the operator
 * presses Send on what the screen shows.
 *
 * THE TEXT IS THE PLATFORM'S OWN TEMPLATE (ClariConnect src/sms/templates.ts at v77, the image live
 * since 8:44 PM 19 Sep 2026), rendered as the platform renders it: the brand, the phrase the template
 * uses for the kit, the link, "Note from our team: <note>" or nothing, "Reply STOP to opt out.", then
 * runs of spaces collapsed and the whole trimmed. The phrase and the link come from the API (the
 * `videos` list on the GET); this file holds the sentence and nothing else, and never builds a URL.
 */

export const VIDEO_TEXT_BRAND = 'Alignment Health Plan';

export interface VideoTextSource {
  /** The kit as the PLATFORM's template names it ("your at-home <phrase>"). */
  phrase: string;
  /** The video page, built by the API from MEMBERCARE_VIDEO_PAGE_BASE_URL. */
  url: string;
}

export function composeVideoText(video: VideoTextSource, note: string): string {
  const n = note.trim();
  const noteLine = n ? `Note from our team: ${n}` : '';
  const body = `${VIDEO_TEXT_BRAND}: A short video on how to do your at-home ${video.phrase}: ${video.url} ${noteLine} Reply STOP to opt out.`;
  return body.replace(/[ \t]{2,}/g, ' ').trim();
}

export const NOTE_MAX = 120;

export type NoteRefusal = 'note_required' | 'note_multiline' | 'note_not_plain' | 'note_too_long';
export type NoteCheck = { ok: true; note: string } | { ok: false; reason: NoteRefusal; message: string };

export const NOTE_WORDING: Record<NoteRefusal, string> = {
  note_required: 'a note is required (1 to 120 characters, one line)',
  note_multiline: 'the note must be one line (no line breaks)',
  note_not_plain: "the note may hold letters, digits, spaces and . , : ; ! ? ' ( ) - only; no links, emails or other symbols",
  note_too_long: `the note is over ${NOTE_MAX} characters`,
};

/** The platform's sanitizeNote character class (videoLinks.ts v77), verbatim. */
const NOTE_PLAIN = /^[\p{L}\p{M}\p{N} .,:;!?'()\-]*$/u;
/** The tokens the platform's sanitizeNote REMOVES: a link, a www. address, an email or handle. */
const NOTE_LINKS = /https?:\/\/|www\.|\S+@\S+/i;

/**
 * The platform sanitizes the note; Care refuses by name whatever it would change (see the API's
 * checkNote, which this mirrors). NFC, whitespace collapsed and trimmed first (benign); a line break
 * refused BEFORE the collapse so a pasted paragraph is never silently joined.
 */
export function checkNote(raw: unknown): NoteCheck {
  const s = typeof raw === 'string' ? raw : '';
  if (/[\r\n]/.test(s)) return { ok: false, reason: 'note_multiline', message: NOTE_WORDING.note_multiline };
  const note = s.normalize('NFC').replace(/\s+/g, ' ').trim();
  if (!note) return { ok: false, reason: 'note_required', message: NOTE_WORDING.note_required };
  if (NOTE_LINKS.test(note) || !NOTE_PLAIN.test(note)) return { ok: false, reason: 'note_not_plain', message: NOTE_WORDING.note_not_plain };
  if (note.length > NOTE_MAX) return { ok: false, reason: 'note_too_long', message: NOTE_WORDING.note_too_long };
  return { ok: true, note };
}

/** The note as the counter counts it: the same normalization the check applies (NFC, one space, trimmed). */
export function normalizedNoteLength(raw: string): number {
  return raw.normalize('NFC').replace(/\s+/g, ' ').trim().length;
}

/** The words the tab prints, in one place. */
export const VIDEO_TEST_WORDING = {
  eyebrow: 'Member Care · Testing',
  title: 'Video (test)',
  lede: 'A temporary tab to test the kit videos by text: pick a person on the test list, pick the video, write a one-line note, check the exact text, press Send. The outcome comes back from the texting platform and is listed below for the day.',
  loading: 'Loading...',
  notEnabled: 'This tab is not enabled for your account.',
  configFailed: (err: string) => `The tab could not be loaded (${err}). Refresh to try again.`,
  platformNotConfigured: 'The platform address and secret are not set on this deployment; Send will not reach the platform.',
  recipients: 'Send to',
  noRecipients: 'The test list is empty on this deployment, so there is nobody to send to.',
  videos: 'Video',
  note: 'Note (one line)',
  counter: (n: number) => `${n} / ${NOTE_MAX}`,
  preview: 'Exact text that will go',
  previewPick: 'Pick a video to see the text.',
  send: 'Send',
  sending: 'Sending...',
  sentToday: 'Sent today',
  nothingToday: 'Nothing sent today.',
  ledgerUnavailable: 'The Sent today list could not be read; the access log still holds every press.',
  columns: { time: 'Time (Pacific)', to: 'To', video: 'Video', link: 'Link', note: 'Note', outcome: 'Outcome' },
  outcome: {
    sent: 'Sent',
    suppressed: 'Not sent: suppressed',
    quiet_hours: 'Queued for texting hours',
    rejected: 'Not sent: rejected',
    'unapproved-template': 'Not sent: unapproved template',
    'live-send-disabled': 'Not sent: live sends disabled',
    'not-configured': 'Not sent: platform not configured',
  } as Record<string, string>,
  replayMark: '(replayed)',
  noAnswer: 'The request did not complete. Check the network and press Send again; the same id is kept so nothing goes twice.',
} as const;
